import { useEffect, useState } from "react";
import { Loader2, Plus, Pencil, Trash2, X, Save, Globe } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const SUGGESTED_PATHS = [
    "/", "/about", "/courses", "/online-classes", "/foreign-students",
    "/jabalpur", "/bhopal", "/nagpur",
    "/home-tuition-mumbai", "/home-tuition-delhi", "/home-tuition-bangalore",
    "/home-tuition-pune", "/home-tuition-hyderabad", "/home-tuition-chennai",
    "/home-tuition-kolkata", "/home-tuition-ahmedabad",
    "/online-tuition-usa", "/online-tuition-uk", "/online-tuition-australia",
    "/online-tuition-canada", "/online-tuition-uae", "/online-tuition-singapore",
    "/online-tuition-saudi-arabia", "/online-tuition-germany",
    "/blog", "/faqs", "/pay-now", "/contact", "/register", "/become-a-tutor",
];

export default function SeoMetaPanel() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [pinging, setPinging] = useState(false);
    const [pingStatus, setPingStatus] = useState(null);

    const load = () => {
        setLoading(true);
        api.get("/admin/seo").then((r) => setItems(r.data || [])).catch(() => {}).finally(() => setLoading(false));
    };
    useEffect(load, []);

    const remove = async (path) => {
        if (!window.confirm(`Remove SEO override for ${path}?`)) return;
        try {
            await api.delete(`/admin/seo?path=${encodeURIComponent(path)}`);
            toast.success("Override removed");
            load();
        } catch {
            toast.error("Failed to delete");
        }
    };

    const pingSitemap = async () => {
        setPinging(true);
        setPingStatus(null);
        try {
            const r = await api.post("/admin/seo/sitemap/ping");
            setPingStatus(r.data || {});
            toast.success("Sitemap refreshed · search engines notified");
        } catch (e) {
            toast.error("Sitemap ping failed");
            setPingStatus({ error: String(e?.response?.data?.detail || e.message || "Failed") });
        } finally {
            setPinging(false);
        }
    };

    if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#1E3A8A]" /></div>;

    const usedPaths = new Set(items.map((i) => i.path));
    const unusedPaths = SUGGESTED_PATHS.filter((p) => !usedPaths.has(p));

    return (
        <div data-testid="admin-seo">
            <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
                <div>
                    <h2 className="font-heading text-2xl font-bold">SEO Meta Overrides</h2>
                    <p className="text-sm text-slate-500 mt-1">Override the default `&lt;title&gt;`, description and keywords for any page. Empty fields fall back to the page's built-in SEO.</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <button
                        type="button"
                        onClick={pingSitemap}
                        disabled={pinging}
                        data-testid="sitemap-ping"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-300 text-slate-700 text-sm font-semibold rounded-lg hover:bg-slate-50 disabled:opacity-50"
                    >
                        <Globe className={`w-4 h-4 ${pinging ? "animate-spin" : ""}`} />
                        {pinging ? "Pinging…" : "Refresh & Ping Sitemap"}
                    </button>
                    <button
                        type="button"
                        onClick={() => setEditing({ path: "", title: "", description: "", keywords: "", og_image: "" })}
                        data-testid="seo-new"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-[#1E3A8A] text-white text-sm font-semibold rounded-lg hover:bg-[#1E40AF]"
                    >
                        <Plus className="w-4 h-4" /> Add Override
                    </button>
                </div>
            </div>

            {pingStatus && (
                <div data-testid="ping-status" className="mb-4 bg-white border border-slate-200 rounded-xl p-4 text-sm">
                    <div className="font-semibold text-slate-800 mb-2">Last sitemap ping</div>
                    <ul className="space-y-1 text-slate-600">
                        {Object.entries(pingStatus).map(([k, v]) => (
                            <li key={k}><span className="font-mono text-xs uppercase text-slate-500 mr-2">{k}</span>{String(v)}</li>
                        ))}
                    </ul>
                </div>
            )}

            {items.length === 0 ? (
                <div className="bg-white border border-slate-200 rounded-xl p-12 text-center text-slate-500">
                    No SEO overrides yet. Each page uses its built-in SEO meta until you override it here.
                </div>
            ) : (
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                                <tr>
                                    <th className="px-4 py-3 text-left">Path</th>
                                    <th className="px-4 py-3 text-left">Title</th>
                                    <th className="px-4 py-3 text-left hidden md:table-cell">Description</th>
                                    <th className="px-4 py-3 text-left">Updated</th>
                                    <th className="px-4 py-3"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {items.map((it) => (
                                    <tr key={it.path} className="hover:bg-slate-50">
                                        <td className="px-4 py-3 font-mono text-xs text-[#1E3A8A]">{it.path}</td>
                                        <td className="px-4 py-3 text-slate-800 max-w-xs truncate">{it.title || <span className="text-slate-400 italic">—</span>}</td>
                                        <td className="px-4 py-3 text-slate-500 hidden md:table-cell max-w-md truncate">{it.description || "—"}</td>
                                        <td className="px-4 py-3 text-xs text-slate-500">{new Date(it.updated_at).toLocaleDateString()}</td>
                                        <td className="px-4 py-3 text-right whitespace-nowrap">
                                            <button onClick={() => setEditing(it)} data-testid={`seo-edit-${it.path}`} className="p-2 hover:bg-blue-50 rounded">
                                                <Pencil className="w-4 h-4 text-slate-500" />
                                            </button>
                                            <button onClick={() => remove(it.path)} data-testid={`seo-delete-${it.path}`} className="p-2 hover:bg-red-50 rounded">
                                                <Trash2 className="w-4 h-4 text-red-500" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {editing && (
                <SeoEditor
                    item={editing}
                    suggestedPaths={editing.path ? [] : unusedPaths}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); load(); }}
                />
            )}
        </div>
    );
}

function SeoEditor({ item, suggestedPaths, onClose, onSaved }) {
    const [form, setForm] = useState({
        path: item.path || "",
        title: item.title || "",
        description: item.description || "",
        keywords: item.keywords || "",
        og_image: item.og_image || "",
    });
    const [saving, setSaving] = useState(false);
    const isEdit = !!item.path;

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const save = async () => {
        if (!form.path || !form.path.startsWith("/")) {
            toast.error("Path must start with '/'");
            return;
        }
        setSaving(true);
        try {
            await api.put("/admin/seo", form);
            toast.success(isEdit ? "Updated" : "Created");
            onSaved();
        } catch {
            toast.error("Failed to save");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="seo-editor">
                <div className="p-6 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
                    <h3 className="font-heading text-lg font-semibold">{isEdit ? `Edit SEO · ${item.path}` : "New SEO Override"}</h3>
                    <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X className="w-5 h-5" /></button>
                </div>
                <div className="p-6 space-y-4">
                    {!isEdit && (
                        <div>
                            <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Page Path *</label>
                            <input
                                value={form.path}
                                onChange={set("path")}
                                placeholder="/jabalpur"
                                data-testid="seo-path"
                                list="seo-paths"
                                className="w-full px-3 py-2.5 text-sm font-mono rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#3B82F6]"
                            />
                            <datalist id="seo-paths">
                                {suggestedPaths.map((p) => <option key={p} value={p} />)}
                            </datalist>
                            <div className="mt-2 flex flex-wrap gap-1.5">
                                {suggestedPaths.slice(0, 12).map((p) => (
                                    <button
                                        key={p}
                                        onClick={() => setForm((f) => ({ ...f, path: p }))}
                                        className="px-2 py-1 text-xs bg-slate-100 hover:bg-blue-100 hover:text-[#1E3A8A] rounded"
                                    >{p}</button>
                                ))}
                            </div>
                        </div>
                    )}
                    <div>
                        <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Page Title (overrides &lt;title&gt;)</label>
                        <input value={form.title} onChange={set("title")} data-testid="seo-title" placeholder="e.g. Best Home Tuition in Jabalpur — Free Demo · Anand Tutorials" className="w-full px-3 py-2.5 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#3B82F6]" />
                        <div className="text-xs text-slate-400 mt-1">Recommended length: 50–60 characters. Current: <span className={form.title.length > 60 ? "text-red-500" : "text-slate-500"}>{form.title.length}</span></div>
                    </div>
                    <div>
                        <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Meta Description</label>
                        <textarea value={form.description} onChange={set("description")} rows={3} data-testid="seo-description" placeholder="One short, punchy description of this page (used in Google snippets)." className="w-full px-3 py-2.5 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#3B82F6]" />
                        <div className="text-xs text-slate-400 mt-1">Recommended: 140–160 characters. Current: <span className={form.description.length > 160 ? "text-red-500" : "text-slate-500"}>{form.description.length}</span></div>
                    </div>
                    <div>
                        <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Meta Keywords</label>
                        <input value={form.keywords} onChange={set("keywords")} data-testid="seo-keywords" placeholder="home tuition jabalpur, best tutor in jabalpur" className="w-full px-3 py-2.5 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#3B82F6]" />
                    </div>
                    <div>
                        <label className="text-xs font-semibold text-slate-600 mb-1.5 block">OG Image URL (optional)</label>
                        <input value={form.og_image} onChange={set("og_image")} data-testid="seo-og-image" placeholder="https://…/your-share-image.png (1200×630)" className="w-full px-3 py-2.5 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#3B82F6]" />
                    </div>
                </div>
                <div className="p-6 border-t border-slate-200 flex justify-end gap-2 sticky bottom-0 bg-white">
                    <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
                    <button onClick={save} disabled={saving} data-testid="seo-save" className="inline-flex items-center gap-2 px-5 py-2 bg-[#1E3A8A] text-white text-sm font-semibold rounded-lg hover:bg-[#1E40AF] disabled:opacity-60">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        {saving ? "Saving…" : "Save"}
                    </button>
                </div>
            </div>
        </div>
    );
}
